import React, { useEffect, useState } from 'react';
import { Card, Form, Button } from 'react-bootstrap';
import config from '../../services/apiConfig';
import { apiPaths } from '../../services/apiPath';
import moment from 'moment';
import { predictEventDateThisYear } from '../../helper';

const CurrentYearScheduleSuggestion = ({ startDate, endDate, onSelectionChange }) => {
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState({});
  const [predictedDates, setPredictedDates] = useState({});
  const [loadingPrediction, setLoadingPrediction] = useState({});
  const [daysWithEvents, setDaysWithEvents] = useState({});

  useEffect(() => {
    const fetchSystemSuggestions = async () => {
      try {
        const lastYearStart = moment(startDate).subtract(1, 'year').format('YYYY-MM-DD');
        const lastYearEnd = moment(endDate).subtract(1, 'year').format('YYYY-MM-DD');
        const response = await fetch(config.BASE_URI + apiPaths.adminData, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            payload: {
              body: {
                query_type: 'fetch_current_year_schedule',
                start_date: moment(startDate).format("DD MMM YYYY"),
                end_date: moment(endDate).format("DD MMM YYYY"),
                last_year_start_date: lastYearStart,
                last_year_end_date: lastYearEnd,
              }
            }
          })
        });

        const data = await response.json();
        if (data.success && Array.isArray(data.events)) {
          const byDate = {};

          data.events.forEach(event => {
            const formattedDate = moment(event.event_date, "DD MMM YYYY").format("YYYY-MM-DD");
            if (!byDate[formattedDate]) {
              byDate[formattedDate] = [];
            }
            byDate[formattedDate].push(event);
          });

          const initialSelected = {};
          data.events.forEach(event => {
            initialSelected[event.event_name] = false;
          });

          setSelected(initialSelected);
          setEvents(data.events);
          setDaysWithEvents(byDate);
        }
      } catch (err) {
        console.error("❌ Failed to fetch current year schedule:", err);
      }
    };

    if (startDate && endDate) fetchSystemSuggestions();
  }, [startDate, endDate]);

  const toggleInclude = (eventName) => {
    const updated = {
      ...selected,
      [eventName]: {
        ...selected[eventName],
        include: !selected[eventName]?.include,
      },
    };
    setSelected(updated);

    const selectedEvents = events
      .filter(event => updated[event.event_name]?.include)
      .map(event => ({
        id: event.event_name,
        event_id: event.event_name,
        event_name: event.event_name,
        location: event.location || 'Current Roster',
        predicted_date: predictedDates[event.event_name] || null,
      }));

    onSelectionChange(selectedEvents);
  };

  const allDatesInRange = [];
  const mStart = moment(startDate);
  const mEnd = moment(endDate);
  for (let m = mStart.clone(); m.isSameOrBefore(mEnd); m.add(1, 'day')) {
    allDatesInRange.push(m.format('YYYY-MM-DD'));
  }

  return (
    <div className="mt-5">
      <h5 className="fw-bold text-secondary">Current Year Roster (by Day)</h5>
      {allDatesInRange.map(date => {
        const weekday = moment(date).format('dddd');
        const eventsForDay = daysWithEvents[date] || [];

        return (
          <Card key={date} className="mb-3 shadow-sm border-light">
            <Card.Body>
              <Card.Title>{weekday} ({moment(date).format("DD MMM YYYY")})</Card.Title>

              {eventsForDay.length > 0 ? (
                eventsForDay.map((event, idx) => {
                  // ✅ Detect partial overlap (clash)
                  const hasClash = eventsForDay.some(otherEvent =>
                    otherEvent.event_name !== event.event_name &&
                    moment(otherEvent.start_time, "hh:mm A").isBefore(moment(event.end_time, "hh:mm A")) &&
                    moment(event.start_time, "hh:mm A").isBefore(moment(otherEvent.end_time, "hh:mm A"))
                  );

                  return (
                    <div key={idx} className="mb-3">
                      <h6>{event.event_name}</h6>
                      <p className="mb-1 text-muted">Location: {event.location || 'N/A'}</p>
                      <p className="mb-1 text-muted">Start: {event.start_time || 'N/A'}</p>
                      <p className="mb-1 text-muted">End: {event.end_time || 'N/A'}</p>

                      {hasClash && (
                        <p className="text-danger fw-bold">⚠️ Clash Detected: Overlapping with another event</p>
                      )}

                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        disabled={loadingPrediction[event.event_name]}
                        onClick={async () => {
                          setLoadingPrediction(prev => ({ ...prev, [event.event_name]: true }));
                          const predicted = await predictEventDateThisYear(event.event_name, "Melbourne");
                          setPredictedDates(prev => {
                            const updated = { ...prev, [event.event_name]: predicted };
                            return updated;
                          });
                          setLoadingPrediction(prev => ({ ...prev, [event.event_name]: false }));
                        }}
                      >
                        {loadingPrediction[event.event_name] ? 'Predicting...' : 'Predict This Year’s Date'}
                      </Button>

                      <Form.Check
                        type="checkbox"
                        checked={selected[event.event_name]?.include || false}
                        onChange={() => toggleInclude(event.event_name)}
                        label="Include in schedule"
                        className="mt-2"
                      />
                    </div>
                  );
                })
              ) : (
                <p className="text-muted">No event scheduled.</p>
              )}
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
};

export default CurrentYearScheduleSuggestion;
